// src/pages/AdminSettings.js
import { useState, useEffect } from 'react';
import { Save, CheckCircle } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { admin } from '../services/api';

function Field({ label, value, onChange, placeholder, type = 'text' }) {
  const theme = useTheme();
  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5"
        style={{ color: theme.textMuted }}>{label}</label>
      <input type={type} value={value || ''} onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-4 py-2.5 rounded-xl border text-sm outline-none transition-colors"
        style={{ backgroundColor: theme.background, borderColor: theme.border, color: theme.text }} />
    </div>
  );
}

function Section({ title, sub, children, onSubmit, saving, saved }) {
  const theme = useTheme();
  return (
    <div className="rounded-2xl overflow-hidden" style={{ backgroundColor: theme.card, boxShadow: theme.isDark ? '0 4px 24px rgba(0,0,0,0.35)' : '0 2px 16px rgba(0,0,0,0.08)' }}>
      <div className="px-6 py-4" style={{ borderBottom: `1px solid ${theme.border}`, backgroundColor: theme.background }}>
        <p className="text-sm font-semibold" style={{ color: theme.text }}>{title}</p>
        {sub && <p className="text-xs mt-0.5" style={{ color: theme.textMuted }}>{sub}</p>}
      </div>
      <form onSubmit={onSubmit} className="p-6 space-y-4">
        {children}
        <div className="flex items-center gap-3 pt-1">
          <button type="submit" disabled={saving}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-60"
            style={{ background: 'linear-gradient(135deg, #00BCD4, #4CAF50)' }}>
            <Save size={14} />
            {saving ? 'Saving...' : 'Save'}
          </button>
          {saved && (
            <span className="flex items-center gap-1 text-sm" style={{ color: '#16a34a' }}>
              <CheckCircle size={14} /> Saved
            </span>
          )}
        </div>
      </form>
    </div>
  );
}

export default function AdminSettings() {
  const theme = useTheme();

  const [bank, setBank] = useState({
    bank_name: '', bank_account_name: '', bank_account_number: '',
    bank_branch_code: '', bank_account_type: '',
  });
  const [pricing, setPricing] = useState({
    monthly_price: '', yearly_price: '', yearly_save_percent: '',
  });
  const [loading,      setLoading]      = useState(true);
  const [bankSaving,   setBankSaving]   = useState(false);
  const [bankSaved,    setBankSaved]    = useState(false);
  const [priceSaving,  setPriceSaving]  = useState(false);
  const [priceSaved,   setPriceSaved]   = useState(false);

  useEffect(() => {
    async function load() {
      const res = await admin.getSettings();
      setLoading(false);
      if (res.success && res.data) {
        const b = res.data.bank    || {};
        const p = res.data.pricing || {};
        setBank({
          bank_name:           b.bank_name           || '',
          bank_account_name:   b.bank_account_name   || '',
          bank_account_number: b.bank_account_number || '',
          bank_branch_code:    b.bank_branch_code    || '',
          bank_account_type:   b.bank_account_type   || '',
        });
        setPricing({
          monthly_price:        p.monthly_price        || '',
          yearly_price:         p.yearly_price         || '',
          yearly_save_percent:  p.yearly_save_percent  || '',
        });
      }
    }
    load();
  }, []);

  async function saveBankDetails(e) {
    e.preventDefault();
    setBankSaving(true);
    setBankSaved(false);
    for (const [key, value] of Object.entries(bank)) {
      await admin.updateSetting(key, value);
    }
    setBankSaving(false);
    setBankSaved(true);
    setTimeout(() => setBankSaved(false), 3000);
  }

  async function savePricing(e) {
    e.preventDefault();
    setPriceSaving(true);
    setPriceSaved(false);
    for (const [key, value] of Object.entries(pricing)) {
      await admin.updateSetting(key, value);
    }
    setPriceSaving(false);
    setPriceSaved(true);
    setTimeout(() => setPriceSaved(false), 3000);
  }

  if (loading) return (
    <div className="flex items-center justify-center h-32">
      <div className="w-6 h-6 rounded-full border-2 border-transparent animate-spin" style={{ borderTopColor: '#00ACC1' }} />
    </div>
  );

  return (
    <div className="max-w-xl mx-auto space-y-5 pb-10">
      <h1 className="text-xl font-bold" style={{ color: theme.text }}>Settings</h1>

      <Section title="Bank Details" sub="Shown to users on the upgrade page for EFT payments"
        onSubmit={saveBankDetails} saving={bankSaving} saved={bankSaved}>
        <Field label="Bank Name"       value={bank.bank_name}           onChange={v => setBank(b => ({...b, bank_name: v}))}           placeholder="e.g. Nedbank" />
        <Field label="Account Name"    value={bank.bank_account_name}   onChange={v => setBank(b => ({...b, bank_account_name: v}))}   placeholder="e.g. Codebooth Pty Ltd" />
        <Field label="Account Number"  value={bank.bank_account_number} onChange={v => setBank(b => ({...b, bank_account_number: v}))} placeholder="e.g. 1335675647" />
        <Field label="Branch Code"     value={bank.bank_branch_code}    onChange={v => setBank(b => ({...b, bank_branch_code: v}))}    placeholder="e.g. 198754" />
        <Field label="Account Type"    value={bank.bank_account_type}   onChange={v => setBank(b => ({...b, bank_account_type: v}))}   placeholder="e.g. Cheque" />
      </Section>

      <Section title="Pricing" sub="Monthly and yearly Pro prices (ZAR)"
        onSubmit={savePricing} saving={priceSaving} saved={priceSaved}>
        <Field label="Monthly Price (R)"   value={pricing.monthly_price}       onChange={v => setPricing(p => ({...p, monthly_price: v}))}       type="number" placeholder="75" />
        <Field label="Yearly Price (R)"    value={pricing.yearly_price}        onChange={v => setPricing(p => ({...p, yearly_price: v}))}        type="number" placeholder="800" />
        <Field label="Yearly Saving (%)"   value={pricing.yearly_save_percent} onChange={v => setPricing(p => ({...p, yearly_save_percent: v}))} type="number" placeholder="11" />
      </Section>
    </div>
  );
}